import ShipmentRemoveStockLotDialog from 'views/inventory/InventoryShipment/components/ShipmentRemoveStockLotDialog';
import { useMemo, useState } from 'react';
import { ShipmentSchema, ShipmentLineSchema } from 'types/clientV2/shipments';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import columns from 'views/kitting/Kit/RelatedShipmentTab/ShipmentLineStockLotsGrid/columns';
import { DatagridNames } from 'types/datagrid';
import EditShipmentLineDialog from 'views/inventory/InventoryShipment/components/EditShipmentLineDialog';
import { isWarehouseUser } from 'utils/userPermissions';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import ShipmentAddStockLotDialog from 'views/inventory/InventoryShipment/components/ShipmentAddStockLotDialog';
import { getTrackingStrings } from 'constants/trackingStrings';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import useGetCustomPropertyColumns from 'hooks/useGetCustomPropertyColumns';
import {
  CustomPropertyModel,
  CustomPropertyVisibility,
} from 'types/customProperty';
import { useUpdateShipmentLineMutation } from 'store/slices/apiV1/inventory';
import { FieldValues } from 'react-hook-form';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { ShipmentLinesReportData } from 'types/generateReports';
import { ALL_RECORD_TYPE_IDS } from 'constants/recordTypes';
import { ShipmentSchemaExpanded } from 'views/kitting/Kit/RelatedShipmentTab';

const InventoryShipmentStockLotTab = ({
  shipment,
  disabled,
  lines,
  showMerge,
  shouldSaveDatagridState,
  isLoading,
  showNewRecordButton = false,
}: {
  shipment: ShipmentSchemaExpanded | null;
  disabled: boolean;
  lines: DecoratedKitRequestLine['shipmentLines'];
  showMerge: boolean;
  shouldSaveDatagridState?: boolean;
  isLoading?: boolean;
  showNewRecordButton?: boolean;
}) => {
  const [activeRowId, setActiveRowId] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addStockLotDialogOpen, setAddStockLotDialogOpen] = useState(false);
  const { data: user } = useGetUserQuery(undefined);
  const { openAddStockLotDialog } = getTrackingStrings();
  const shipmentShipFromName =
    shipment?.shipFrom?.externalName || shipment?.shipFrom?.name || '';

  const { customPropertyColumns } = useGetCustomPropertyColumns({
    model: CustomPropertyModel.SHIPMENT_LINE,
    visibilityContext: CustomPropertyVisibility.DATAGRID_EDIT,
    useMutation: useUpdateShipmentLineMutation,
    getMutationArgs: (data: FieldValues, params: GridRenderCellParams) => ({
      shipmentId: params.row.shipment,
      shipmentLineId: params.row.id,
      payload: data,
    }),
  });

  const memoizedColumns = useMemo(
    () => [...columns, ...customPropertyColumns],
    [customPropertyColumns]
  );

  return (
    <>
      <ExtendedDatagrid<ShipmentLineSchema, ShipmentLinesReportData>
        recordType={ALL_RECORD_TYPE_IDS.SHIPMENT_LINE}
        loading={isLoading}
        exportFileNamePrefix={`${shipmentShipFromName}-Shipment-StockLots`}
        noRows="Add one or more stock lots to this shipment."
        gridName={DatagridNames.kitRequestLineShipmentLines}
        shouldSaveDatagridState={shouldSaveDatagridState}
        rows={lines}
        columns={memoizedColumns}
        checkboxSelection={showMerge && isWarehouseUser(user, shipment?.org)}
        editable
        editLocked={disabled}
        editMode="cell"
        initialVisibleColumns={[
          'part',
          'status',
          'shippedQuant',
          'package',
          'printedNotes',
          'actions',
        ]}
        newRecordButton={
          showNewRecordButton
            ? {
                label: 'Add Stock Lot',
                permissionScope: {
                  app: ALL_APPS.KITTING.id,
                  accessLevel: appPermissionAccessLevels.edit,
                },
                onClick: () => {
                  setAddStockLotDialogOpen(true);
                },
                trackingName: openAddStockLotDialog,
              }
            : undefined
        }
        initialSorting={{
          sortModel: [{ field: 'part', sort: 'asc' }],
        }}
        generateReportData={
          shipment
            ? {
                shipment: shipment as unknown as ShipmentSchema,
                lines: lines as unknown as ShipmentLineSchema[],
              }
            : undefined
        }
      />
      <ShipmentRemoveStockLotDialog
        lineId={activeRowId}
        dialogOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      />
      {shipment && (
        <EditShipmentLineDialog
          shipmentId={shipment.id}
          lineId={activeRowId}
          dialogOpen={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          excludedStockLotIds={lines.map((line) => line.stockLot?.id) || []}
        />
      )}
      {shipment && (
        <ShipmentAddStockLotDialog
          open={addStockLotDialogOpen}
          onClose={() => setAddStockLotDialogOpen(false)}
          shipmentId={shipment.id}
          excludedStockLotIds={lines.map((line) => line.stockLot?.id) || []}
        />
      )}
    </>
  );
};

export default InventoryShipmentStockLotTab;
